import EventEmitter from "../../utils/event-emitter";
import {extend, borders, uuid, isNumber, bounds, defer, createBlobUrl, revokeBlobUrl} from "../../utils/core";
import EpubCFI from "../../epubcfi";
import Contents from "../../contents";
import { EVENTS } from "../../utils/constants";
import { Pane, Highlight, Underline } from "../../marks-pane";
import type { Mark } from "../../marks-pane";
import type { IEventEmitter, ViewSettings, ReframeBounds, RequestFunction } from "../../types";
import type Section from "../../section";
import type Layout from "../../layout";
import { sectionLayoutName } from "../../layout";

export interface IframeViewEvents extends Record<string, any[]> {
	"axis": [string];
	"writingMode": [string];
	"loaderror": [unknown];
	"rendered": [Section];
	"resized": [ReframeBounds];
	"displayed": [IframeView];
	"shown": [IframeView];
	"hidden": [IframeView];
	"markClicked": [string, object | undefined];
}

class IframeView implements IEventEmitter<IframeViewEvents> {
	settings: ViewSettings;
	id: string;
	section: Section;
	index: number;
	element: HTMLElement;
	added: boolean;
	displayed: boolean;
	rendered: boolean;
	fixedWidth: number;
	fixedHeight: number;
	epubcfi: EpubCFI;
	layout: Layout;
	pane: Pane | undefined;
	highlights: Record<string, { mark: Mark; element: SVGElement | null; listeners: (EventListener | undefined)[] }>;
	underlines: Record<string, { mark: Mark; element: SVGElement | null; listeners: (EventListener | undefined)[] }>;
	marks: Record<string, { element: HTMLAnchorElement; range: Range; listeners: (EventListener | undefined)[] }>;
	iframe: HTMLIFrameElement | undefined;
	resizing!: boolean;
	_width: number | undefined;
	_height: number | undefined;
	_textWidth: number | undefined;
	_textHeight: number | undefined;
	_contentWidth: number | undefined;
	_contentHeight: number | undefined;
	_contentDirty: boolean = true;
	_needsReframe: boolean = false;
	_expanding: boolean = false;
	elementBounds!: { width: number; height: number };
	supportsSrcdoc!: boolean;
	sectionRender: Promise<string> | undefined;
	lockedWidth!: number;
	lockedHeight!: number;
	prevBounds: ReframeBounds | undefined;
	blobUrl?: string;
	document!: Document;
	window!: Window;
	contents: Contents | undefined;
	rendering!: boolean;
	writingMode!: string;
	stopExpanding!: boolean;
	axis!: string;
	expanded?: boolean;
	_abortController: AbortController | undefined;
	_displaying: Promise<IframeView> | undefined;
	_loading: defer<Contents> | undefined;

	declare on: IEventEmitter<IframeViewEvents>["on"];
	declare off: IEventEmitter<IframeViewEvents>["off"];
	declare emit: IEventEmitter<IframeViewEvents>["emit"];
	declare __listeners: IEventEmitter<IframeViewEvents>["__listeners"];

	constructor(section: Section, options?: ViewSettings) {
		this.settings = extend({
			ignoreClass : "",
			axis: undefined, //options.layout && options.layout.props.flow === "scrolled" ? "vertical" : "horizontal",
			direction: undefined,
			width: 0,
			height: 0,
			layout: undefined,
			globalLayoutProperties: {},
			method: undefined,
			forceRight: false,
			allowScriptedContent: false,
			allowPopups: false
		} as ViewSettings, options || {});

		this.id = "epubjs-view-" + uuid();
		this.section = section;
		this.index = section.index!;

		this.element = this.container(this.settings.axis);

		this.added = false;
		this.displayed = false;
		this.rendered = false;

		// this.width  = this.settings.width;
		// this.height = this.settings.height;

		this.fixedWidth  = 0;
		this.fixedHeight = 0;

		// Blank Cfi for Parsing
		this.epubcfi = new EpubCFI();

		this.layout = this.settings.layout as unknown as Layout;
		// Dom events to listen for
		// this.listenedEvents = ["keydown", "keyup", "keypressed", "mouseup", "mousedown", "click", "touchend", "touchstart"];

		this.pane = undefined;
		this.highlights = {};
		this.underlines = {};
		this.marks = {};

	}

	private get isFixedLayout(): boolean {
		return sectionLayoutName(this.section, this.layout.name) === "pre-paginated";
	}

	container(axis?: string): HTMLElement {
		const element = document.createElement("div");

		element.classList.add("epub-view");

		// this.element.style.minHeight = "100px";
		element.style.height = "0px";
		element.style.width = "0px";
		element.style.overflow = "hidden";
		element.style.position = "relative";
		element.style.display = "block";
		// Isolate each view's reflow/paint from its siblings. Do NOT include
		// `size` — `expand()` needs to read the content's natural dimensions.
		element.style.contain = "layout paint";

		if(axis && axis === "horizontal"){
			element.style.flex = "none";
		} else {
			element.style.flex = "initial";
		}

		return element;
	}

	create(): HTMLIFrameElement {

		if(this.iframe) {
			return this.iframe;
		}

		if(!this.element) {
			this.element = this.container();
		}

		this.iframe = document.createElement("iframe");
		this.iframe.id = this.id;
		this.iframe.scrolling = "no"; // Might need to be removed: breaks ios width calculations
		this.iframe.style.overflow = "hidden";
		this.iframe.setAttribute("seamless", "seamless");
		// Back up if seamless isn't supported
		this.iframe.style.border = "none";

		// sandbox
		this.iframe.sandbox = "allow-same-origin";
		if (this.settings.allowScriptedContent) {
			this.iframe.sandbox += " allow-scripts";
		}
		if (this.settings.allowPopups) {
			this.iframe.sandbox += " allow-popups";
		}

		this.iframe.setAttribute("enable-annotation", "true");

		this.resizing = true;

		// this.iframe.style.display = "none";
		this.element.style.visibility = "hidden";
		this.iframe.style.visibility = "hidden";

		this.iframe.style.width = "0";
		this.iframe.style.height = "0";
		this._width = 0;
		this._height = 0;

		this.element.setAttribute("ref", String(this.index));

		this.added = true;

		this.elementBounds = bounds(this.element);

		// if(width || height){
		//   this.resize(width, height);
		// } else if(this.width && this.height){
		//   this.resize(this.width, this.height);
		// } else {
		//   this.iframeBounds = bounds(this.iframe);
		// }


		if(("srcdoc" in this.iframe)) {
			this.supportsSrcdoc = true;
		} else {
			this.supportsSrcdoc = false;
		}

		if (!this.settings.method) {
			this.settings.method = this.supportsSrcdoc ? "srcdoc" : "write";
		}

		return this.iframe;
	}

	render(request: RequestFunction, _show?: boolean): Promise<void> {

		// view.onLayout = this.layout.format.bind(this.layout);
		this.create();

		// Fit to size of the container, apply padding
		this.size();

		if (typeof AbortController !== "undefined" && !this._abortController) {
			this._abortController = new AbortController();
		}
		const signal = this._abortController?.signal;

		if(!this.sectionRender) {
			this.sectionRender = this.section.render(request, signal);
		}

		// Render Chain
		return this.sectionRender
			.then((contents: string) => {
				if (signal?.aborted) {
					return Promise.reject(new DOMException("Aborted", "AbortError"));
				}
				return this.load(contents);
			})
			.then(() => {

				// find and report the writingMode axis
				const writingMode = this.contents!.writingMode();

				// Set the axis based on the flow and writing mode
				let axis;
				if (this.settings.flow === "scrolled") {
					axis = writingMode.startsWith("vertical") ? "horizontal" : "vertical";
				} else {
					axis = writingMode.startsWith("vertical") ? "vertical" : "horizontal";
				}

				if (writingMode.startsWith("vertical") && this.settings.flow === "paginated") {
					this.layout.delta = this.layout.height;
				}

				this.setAxis(axis);
				this.emit(EVENTS.VIEWS.AXIS, axis);

				this.setWritingMode(writingMode);
				this.emit(EVENTS.VIEWS.WRITING_MODE, writingMode);


				// apply the layout function to the contents
				this.layout.format(this.contents!, this.section, this.axis);

				// Listen for events that require an expansion of the iframe
				this.addListeners();

				return new Promise<void>((resolve, _reject) => {
					// Expand the iframe to the full size of the content
					this.expand();

					if (this.settings.forceRight) {
						this.element.style.marginLeft = this.width() + "px";
					}
					resolve();
				});

			}, (e: Error) => {
				// A rejected promise stays rejected — cached, it would fail every later render().
				this.sectionRender = undefined;

				if (signal?.aborted || e.name === "AbortError") {
					return Promise.reject(e);
				}
				this.emit(EVENTS.VIEWS.LOAD_ERROR, e);
				return new Promise((resolve, reject) => {
					reject(e);
				});
			})
			.then(() => {
				this.emit(EVENTS.VIEWS.RENDERED, this.section);
			});

	}

	reset (): void {
		if (this.iframe) {
			this.iframe.style.width = "0";
			this.iframe.style.height = "0";
			this._width = 0;
			this._height = 0;
			this._textWidth = undefined;
			this._contentWidth = undefined;
			this._textHeight = undefined;
			this._contentHeight = undefined;
		}
		this._contentDirty = true;
		this._needsReframe = true;
	}

	// Determine locks base on settings
	size(_width?: number, _height?: number): void {
		const width = _width || this.settings.width!;
		const height = _height || this.settings.height!;

		if(this.isFixedLayout) {
			this.lock("both", width, height);
		} else if(this.settings.axis === "horizontal") {
			this.lock("height", width, height);
		} else {
			this.lock("width", width, height);
		}

		this.settings.width = width;
		this.settings.height = height;
	}

	// Lock an axis to element dimensions, taking borders into account
	lock(what: string, width: number, height: number): void {
		const elBorders = borders(this.element);
		let iframeBorders;

		if(this.iframe) {
			iframeBorders = borders(this.iframe);
		} else {
			iframeBorders = {width: 0, height: 0};
		}

		if(what === "width" && isNumber(width)){
			this.lockedWidth = width - elBorders.width - iframeBorders.width;
			// this.resize(this.lockedWidth, width); //  width keeps ratio correct
		}

		if(what === "height" && isNumber(height)){
			this.lockedHeight = height - elBorders.height - iframeBorders.height;
			// this.resize(width, this.lockedHeight);
		}

		if(what === "both" &&
			 isNumber(width) &&
			 isNumber(height)){

			this.lockedWidth = width - elBorders.width - iframeBorders.width;
			this.lockedHeight = height - elBorders.height - iframeBorders.height;
			// this.resize(this.lockedWidth, this.lockedHeight);
		}

		if(this.displayed && this.iframe) {

			// this.contents.layout();
			this.expand();
		}



	}

	// Resize a single axis based on content dimensions
	expand(_force?: boolean): void {
		let width = this.lockedWidth;
		let height = this.lockedHeight;
		let columns;

		if(!this.iframe || this._expanding) return;

		this._expanding = true;

		if(this.isFixedLayout) {
			width = this.layout.columnWidth;
			height = this.layout.height;
		}
		// Expand Horizontally
		else if(this.settings.axis === "horizontal") {
			// Use cached text width when content hasn't changed (avoids synchronous reflow)
			if (!this._contentDirty && this._textWidth !== undefined) {
				width = this._textWidth;
			} else {
				width = this.contents!.textWidth();
				this._textWidth = width;
				this._contentDirty = false;
			}

			if (width % this.layout.pageWidth > 0) {
				width = Math.ceil(width / this.layout.pageWidth) * this.layout.pageWidth;
			}

			if (this.settings.forceEvenPages) {
				columns = (width / this.layout.pageWidth);
				if ( this.layout.divisor > 1 &&
						 this.layout.name === "reflowable" &&
						(columns % 2 > 0)) {
					// add a blank page
					width += this.layout.pageWidth;
				}
			}

		} // Expand Vertically
		else if(this.settings.axis === "vertical") {
			// Use cached text height when content hasn't changed (avoids synchronous reflow)
			if (!this._contentDirty && this._textHeight !== undefined) {
				height = this._textHeight;
			} else {
				height = this.contents!.textHeight();
				this._textHeight = height;
				this._contentDirty = false;
			}

			if (this.settings.flow === "paginated" &&
				height % this.layout.height > 0) {
				height = Math.ceil(height / this.layout.height) * this.layout.height;
			}
		}

		// Only Resize if dimensions have changed or
		// if Frame is still hidden, so needs reframing
		if(this._needsReframe || width !== this._width || height !== this._height){
			this.reframe(width, height);
		}

		this._expanding = false;
	}

	reframe(width: number, height: number): void {
		if(isNumber(width)){
			this.element.style.width = width + "px";
			this.iframe!.style.width = width + "px";
			this._width = width;
		}

		if(isNumber(height)){
			this.element.style.height = height + "px";
			this.iframe!.style.height = height + "px";
			this._height = height;
		}

		const widthDelta = this.prevBounds ? width - this.prevBounds.width : width;
		const heightDelta = this.prevBounds ? height - this.prevBounds.height : height;

		const size = {
			width: width,
			height: height,
			widthDelta: widthDelta,
			heightDelta: heightDelta,
		};

		this.pane && this.pane.render();

		if (Object.keys(this.marks).length > 0) {
			requestAnimationFrame(() => {
				let mark;
				for (const m in this.marks) {
					if (Object.prototype.hasOwnProperty.call(this.marks, m)) {
						mark = this.marks[m]!;
						this.placeMark(mark.element, mark.range);
					}
				}
			});
		}

		this.onResize(this, size);

		this.emit(EVENTS.VIEWS.RESIZED, size);

		this.prevBounds = size;

		this.elementBounds = bounds(this.element);

	}


	load(contents: string): Promise<Contents> {
		const loading = new defer<Contents>();
		const loaded = loading.promise;

		if(!this.iframe) {
			loading.reject(new Error("No Iframe Available"));
			return loaded;
		}

		// Held so destroy() can settle it: the iframe's load event is the only
		// other way out, and a destroyed view may never receive one.
		this._loading = loading;

		const onload = (event: Event): void => {

			this.onLoad(event, loading);

		};

		if (this.settings.method === "blobUrl") {
			this.iframe.onload = onload;
			// A view can be re-displayed while its first load is still in flight —
			// the continuous manager reuses instances — so drop the previous blob
			// instead of orphaning it for the lifetime of the document.
			if (this.blobUrl) {
				revokeBlobUrl(this.blobUrl);
			}
			this.blobUrl = createBlobUrl(contents, "application/xhtml+xml");
			this.iframe.src = this.blobUrl;
			this.element.appendChild(this.iframe);
		} else if(this.settings.method === "srcdoc"){
			this.iframe.onload = onload;
			this.iframe.srcdoc = contents;
			this.element.appendChild(this.iframe);
		} else {

			this.element.appendChild(this.iframe);

			this.document = this.iframe.contentDocument!;

			if(!this.document) {
				this._loading = undefined;
				loading.reject(new Error("No Document Available"));
				return loaded;
			}

			// Must be attached after appendChild — a src-less iframe fires a load
			// event for its initial about:blank document, and measuring that resolves
			// writingMode() to "" and loses the section's stylesheets. Must be before
			// close() — WebKit fires the written document's load event before its
			// stylesheets have applied.
			this.iframe.onload = onload;

			this.document.open();
			this.document.write(contents);
			this.document.close();

		}

		return loaded;
	}

	onLoad(event: Event, promise: { resolve: (value: Contents | PromiseLike<Contents>) => void; reject: (reason?: unknown) => void }): void {

		this.window = this.iframe!.contentWindow!;
		this.document = this.iframe!.contentDocument!;

		this.contents = new Contents(this.document, this.document.body, this.section.cfiBase, this.section.index);

		this.rendering = false;

		let link = this.document.querySelector("link[rel='canonical']");
		if (link) {
			link.setAttribute("href", this.section.canonical);
		} else {
			link = this.document.createElement("link");
			link.setAttribute("rel", "canonical");
			link.setAttribute("href", this.section.canonical);
			this.document.querySelector("head")!.appendChild(link);
		}

		this.contents.on(EVENTS.CONTENTS.EXPAND, () => {
			if(this.displayed && this.iframe) {
				this._contentDirty = true;
				this.expand();
				if (this.contents) {
					this.layout.format(this.contents, this.section);
				}
			}
		});

		this.contents.on(EVENTS.CONTENTS.RESIZE, (e: { width: number; height: number }) => {
			if(this.displayed && this.iframe) {
				// Pre-populate cache with values already measured by resizeCheck(),
				// avoiding a redundant reflow when expand() runs next
				this._textWidth = e.width;
				this._textHeight = e.height;
				this._contentDirty = false;
				this.expand();
				if (this.contents) {
					this.layout.format(this.contents, this.section);
				}
			}
		});

		this._loading = undefined;
		promise.resolve(this.contents);
	}

	setLayout(layout: Layout): void {
		this.layout = layout;

		if (this.contents) {
			this.layout.format(this.contents, this.section);
			this._contentDirty = true;
			this.expand();
		}
	}

	setAxis(axis: string): void {

		this.settings.axis = axis;

		if(axis === "horizontal"){
			this.element.style.flex = "none";
		} else {
			this.element.style.flex = "initial";
		}

		this.size();

	}

	setWritingMode(mode: string): void {
		// this.element.style.writingMode = writingMode;
		this.writingMode = mode;
	}

	addListeners(): void {
	}

	removeListeners(): void {
		if (this.contents) {
			this.contents.off(EVENTS.CONTENTS.EXPAND);
			this.contents.off(EVENTS.CONTENTS.RESIZE);
		}
	}

	/** True while a display() is in flight — rendered but not yet displayed. */
	get displaying(): boolean {
		return this._displaying !== undefined;
	}

	display(request: RequestFunction): Promise<IframeView> {
		if (this.displayed) {
			return Promise.resolve(this);
		}

		// `displayed` only flips once render() resolves, so overlapping calls
		// would each reach load() and overwrite iframe.onload — orphaning every
		// deferred but the last. Share the in-flight promise instead.
		if (this._displaying) {
			return this._displaying;
		}

		const displayed = new defer<IframeView>();
		this._displaying = displayed.promise;

		this.render(request)
			.then(() => {
				// Settle before emitting, and catch terminally: the emit and
				// onDisplayed below run application code. A listener that throws
				// would otherwise leave _displaying set, which the in-flight
				// sharing above hands to every later display(), and would leave
				// `displayed` false — which is what views.show() gates on, so a
				// fully rendered iframe would stay hidden for good.
				this.displayed = true;
				this._displaying = undefined;
				displayed.resolve(this);

				this.emit(EVENTS.VIEWS.DISPLAYED, this);
				this.onDisplayed(this);

			})
			.catch((err: Error) => {
				this._displaying = undefined;
				displayed.reject(err);
			});

		return displayed.promise;
	}

	show(): void {

		this.element.style.visibility = "visible";

		if(this.iframe){
			this.iframe.style.visibility = "visible";

			// Remind Safari to redraw the iframe
			this.iframe.style.transform = "translateZ(0)";
			this.iframe.offsetWidth;
			this.iframe.style.transform = "";
		}

		this.emit(EVENTS.VIEWS.SHOWN, this);
	}

	hide(): void {
		// this.iframe.style.display = "none";
		this.element.style.visibility = "hidden";
		this.iframe!.style.visibility = "hidden";

		this.stopExpanding = true;
		this.emit(EVENTS.VIEWS.HIDDEN, this);
	}

	offset(): { top: number; left: number } {
		return {
			top: this.element.offsetTop,
			left: this.element.offsetLeft
		}
	}

	width(): number {
		return this._width!;
	}

	height(): number {
		return this._height!;
	}

	position(): DOMRect {
		return this.element.getBoundingClientRect();
	}

	locationOf(target: string): { left: number; top: number } {
		const _parentPos = this.iframe!.getBoundingClientRect();
		const targetPos = this.contents!.locationOf(target, this.settings.ignoreClass);

		return {
			"left": targetPos.left,
			"top": targetPos.top
		};
	}

	onDisplayed(_view: IframeView): void {
		// Stub, override with a custom functions
	}

	onResize(_view: IframeView, _e?: ReframeBounds): void {
		// Stub, override with a custom functions
	}

	bounds(force?: boolean): { width: number; height: number } {
		if(force || !this.elementBounds) {
			this.elementBounds = bounds(this.element);
		}

		return this.elementBounds;
	}

	highlight(cfiRange: string, data: Record<string, string> = {}, cb?: EventListener, className: string = "epubjs-hl", styles: Record<string, string> = {}): Mark | undefined {
		if (!this.contents) {
			return;
		}
		const attributes = Object.assign({"fill": "yellow", "fill-opacity": "0.3", "mix-blend-mode": "multiply"}, styles);
		const range = this.contents.range(cfiRange);

		const emitter: EventListener = (_e: Event): void => {
			this.emit(EVENTS.VIEWS.MARK_CLICKED, cfiRange, data);
		};

		data["epubcfi"] = cfiRange;

		if (!this.pane) {
			this.pane = new Pane(this.iframe!, this.element);
		}

		const m = new Highlight(range, className, data, attributes);
		let h: Mark;
		try {
			h = this.pane.addMark(m);
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error("Failed to add highlight for", cfiRange, e);
			return;
		}

		this.highlights[cfiRange] = { "mark": h, "element": h.element, "listeners": [emitter, cb] };

		h.element!.setAttribute("ref", className);
		h.element!.addEventListener("click", emitter);
		h.element!.addEventListener("touchstart", emitter);

		if (cb) {
			h.element!.addEventListener("click", cb);
			h.element!.addEventListener("touchstart", cb);
		}
		return h;
	}

	underline(cfiRange: string, data: Record<string, string> = {}, cb?: EventListener, className: string = "epubjs-ul", styles: Record<string, string> = {}): Mark | undefined {
		if (!this.contents) {
			return;
		}
		const attributes = Object.assign({"stroke": "black", "stroke-opacity": "0.3", "mix-blend-mode": "multiply"}, styles);
		const range = this.contents.range(cfiRange);
		const emitter: EventListener = (_e: Event): void => {
			this.emit(EVENTS.VIEWS.MARK_CLICKED, cfiRange, data);
		};

		data["epubcfi"] = cfiRange;

		if (!this.pane) {
			this.pane = new Pane(this.iframe!, this.element);
		}

		const m = new Underline(range, className, data, attributes);
		let h: Mark;
		try {
			h = this.pane.addMark(m);
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error("Failed to add underline for", cfiRange, e);
			return;
		}

		this.underlines[cfiRange] = { "mark": h, "element": h.element, "listeners": [emitter, cb] };

		h.element!.setAttribute("ref", className);
		h.element!.addEventListener("click", emitter);
		h.element!.addEventListener("touchstart", emitter);

		if (cb) {
			h.element!.addEventListener("click", cb);
			h.element!.addEventListener("touchstart", cb);
		}
		return h;
	}

	mark(cfiRange: string, data: Record<string, string> = {}, cb?: EventListener): Node | { element: HTMLAnchorElement; range: Range; listeners: (EventListener | undefined)[] } | null | undefined {
		if (!this.contents) {
			return;
		}

		if (cfiRange in this.marks) {
			const item = this.marks[cfiRange];
			return item;
		}

		let range = this.contents.range(cfiRange);
		if (!range) {
			return;
		}
		const container = range.commonAncestorContainer;
		const parent = (container.nodeType === 1) ? container : container.parentNode;

		const emitter: EventListener = (_e: Event): void => {
			this.emit(EVENTS.VIEWS.MARK_CLICKED, cfiRange, data);
		};

		if (range.collapsed && container.nodeType === 1) {
			range = new Range();
			range.selectNodeContents(container);
		} else if (range.collapsed) { // Webkit doesn't like collapsed ranges
			range = new Range();
			range.selectNodeContents(parent!);
		}

		const mark = this.document.createElement("a");
		mark.setAttribute("ref", "epubjs-mk");
		mark.style.position = "absolute";

		mark.dataset["epubcfi"] = cfiRange;

		if (data) {
			Object.keys(data).forEach((key) => {
				mark.dataset[key] = data[key];
			});
		}

		if (cb) {
			mark.addEventListener("click", cb);
			mark.addEventListener("touchstart", cb);
		}

		mark.addEventListener("click", emitter);
		mark.addEventListener("touchstart", emitter);

		this.placeMark(mark, range);

		this.element.appendChild(mark);

		this.marks[cfiRange] = { "element": mark, "range": range, "listeners": [emitter, cb] };

		return parent;
	}

	placeMark(element: HTMLElement, range: Range): void {
		let top, right, left;

		if(this.isFixedLayout ||
			this.settings.axis !== "horizontal") {
			const pos = range.getBoundingClientRect();
			top = pos.top;
			right = pos.right;
		} else {
			// Element might break columns, so find the left most element
			const rects = range.getClientRects();

			let rect;
			for (let i = 0; i !== rects.length; i++) {
				rect = rects[i]!;
				if (!left || rect.left < left) {
					left = rect.left;
					// right = rect.right;
					right = Math.ceil(left / this.layout.props.pageWidth!) * this.layout.props.pageWidth! - (this.layout.gap / 2);
					top = rect.top;
				}
			}
		}

		element.style.top = `${top}px`;
		element.style.left = `${right}px`;
	}

	unhighlight(cfiRange: string): void {
		if (cfiRange in this.highlights) {
			const item = this.highlights[cfiRange]!;

			this.pane!.removeMark(item.mark);
			item.listeners.forEach((l: EventListener | undefined) => {
				if (l) {
					item.element!.removeEventListener("click", l);
					item.element!.removeEventListener("touchstart", l);
				};
			});
			delete this.highlights[cfiRange];
		}
	}

	ununderline(cfiRange: string): void {
		if (cfiRange in this.underlines) {
			const item = this.underlines[cfiRange]!;
			this.pane!.removeMark(item.mark);
			item.listeners.forEach((l: EventListener | undefined) => {
				if (l) {
					item.element!.removeEventListener("click", l);
					item.element!.removeEventListener("touchstart", l);
				};
			});
			delete this.underlines[cfiRange];
		}
	}

	unmark(cfiRange: string): void {
		if (cfiRange in this.marks) {
			const item = this.marks[cfiRange]!;
			this.element.removeChild(item.element);
			item.listeners.forEach((l: EventListener | undefined) => {
				if (l) {
					item.element.removeEventListener("click", l);
					item.element.removeEventListener("touchstart", l);
				};
			});
			delete this.marks[cfiRange];
		}
	}

	destroy(): void {

		if (this._abortController) {
			this._abortController.abort();
			this._abortController = undefined;
		}

		// Both must run even when the view never displayed: the load event is
		// the only thing that settles _loading, and a destroyed view either
		// never gets one or gets one that would resurrect it.
		if (this.iframe) {
			this.iframe.onload = null;
		}

		if (this._loading) {
			this._loading.reject(new DOMException("Aborted", "AbortError"));
			this._loading = undefined;
		}

		this._displaying = undefined;
		this.sectionRender = undefined;

		for (const cfiRange in this.highlights) {
			this.unhighlight(cfiRange);
		}

		for (const cfiRange in this.underlines) {
			this.ununderline(cfiRange);
		}

		for (const cfiRange in this.marks) {
			this.unmark(cfiRange);
		}

		if (this.blobUrl) {
			revokeBlobUrl(this.blobUrl);
			this.blobUrl = undefined;
		}

		if(this.displayed){
			this.displayed = false;

			this.removeListeners();
			this.contents!.destroy();

			this.stopExpanding = true;

			this.element.removeChild(this.iframe!);

			if (this.pane) {
				this.pane.element.remove();
				this.pane = undefined;
			}

			this.iframe = undefined;
			this.contents = undefined;

			this.section.unload();

			this._textWidth = undefined;
			this._textHeight = undefined;
			this._width = undefined;
			this._height = undefined;
		}

		this.__listeners = {};
	}
}

EventEmitter(IframeView.prototype);

export default IframeView;
